import {
  BILLING_INTERVALS,
  MAX_PRICE_CENTS,
  MAX_TRIAL_DAYS,
  SKU_PATTERN,
  normalizeSku,
} from '@oefy/core';
import { z } from 'zod';
import { schoolGroupSchema, timestampSchema } from './common';
import { priceVariantSchema } from './price-test';

export const SKU_RULE = 'A shop code starts with OEFY- and uses only letters, digits, - and _';

export const skuSchema = z
  .string()
  .transform(normalizeSku)
  .pipe(z.string().regex(SKU_PATTERN, SKU_RULE));

export const priceCentsSchema = z.int().min(1).max(MAX_PRICE_CENTS);
export const productNameSchema = z.string().trim().min(1).max(80);
export const billingIntervalSchema = z.enum(BILLING_INTERVALS);
export const priceStageSchema = z.enum(['past', 'current', 'scheduled']);
export const trialDaysSchema = z.int().min(1).max(MAX_TRIAL_DAYS);
export const trialReminderDaysSchema = z
  .int()
  .min(1)
  .max(MAX_TRIAL_DAYS - 1);

export const productPriceSchema = z.object({
  id: z.uuid(),
  amountCents: z.int(),
  currency: z.string(),
  variant: z.string().nullable(),
  startsAt: timestampSchema,
  endsAt: timestampSchema.nullable(),
  stage: priceStageSchema,
  subscribers: z.int().min(0),
  total: z.int().min(0),
});

export const productRowSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  interval: billingIntervalSchema,
  active: z.boolean(),
  onSalesPage: z.boolean(),
  includesBookPackage: z.boolean(),
  trialDays: z.int().nullable(),
  trialReminderDays: z.int().nullable(),
  skus: z.array(z.string()),
  currentPrice: productPriceSchema.nullable(),
  nextPrice: productPriceSchema.nullable(),
  subscribers: z.int().min(0),
  families: z.int().min(0),
  deletable: z.boolean(),
  createdAt: timestampSchema,
});

export const productListSchema = z.object({
  products: z.array(productRowSchema),
  shopConnected: z.boolean(),
});

export const productDetailSchema = productRowSchema.extend({
  prices: z.array(productPriceSchema),
  scheduledReminders: z.int().min(0),
  updatedAt: timestampSchema,
});

export const productCreateSchema = z.object({
  name: productNameSchema,
  interval: billingIntervalSchema,
  sku: skuSchema,
  amountCents: priceCentsSchema,
  trialDays: trialDaysSchema.nullable().default(null),
  trialReminderDays: trialReminderDaysSchema.nullable().optional(),
  onSalesPage: z.boolean().default(true),
});

export const productUpdateSchema = z
  .object({
    name: productNameSchema.optional(),
    active: z.boolean().optional(),
    onSalesPage: z.boolean().optional(),
    includesBookPackage: z.boolean().optional(),
    trialDays: trialDaysSchema.nullable().optional(),
    trialReminderDays: trialReminderDaysSchema.nullable().optional(),
  })
  .refine((input) => Object.keys(input).length > 0, {
    message: 'Change at least one setting',
  });

export const bookPackageSkuSchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z0-9._-]+$/, 'A shop code uses only letters, digits, ., - and _');

export const bookPackagesSchema = z.object({
  packages: z.array(z.object({ schoolGroup: schoolGroupSchema, sku: z.string().nullable() })),
});

export const bookPackagesInputSchema = z.object({
  packages: z
    .array(z.object({ schoolGroup: schoolGroupSchema, sku: bookPackageSkuSchema.nullable() }))
    .max(6),
});

export const skuInputSchema = z.object({ sku: skuSchema });

export const skuParamsSchema = z.object({ id: z.uuid(), sku: skuSchema });

export const priceParamsSchema = z.object({ id: z.uuid(), priceId: z.uuid() });

export const priceInputSchema = z.object({
  amountCents: priceCentsSchema,
  startsAt: timestampSchema.nullable(),
  variant: priceVariantSchema.nullable().default(null),
});

export const shopPriceCheckSchema = z.object({
  connected: z.boolean(),
  error: z.string().nullable(),
  checks: z.array(
    z.discriminatedUnion('status', [
      z.object({
        sku: z.string(),
        status: z.enum(['match', 'mismatch']),
        shopName: z.string(),
        shopCents: z.int(),
        saleCents: z.int().nullable(),
      }),
      z.object({ sku: z.string(), status: z.literal('missing') }),
    ]),
  ),
});

export type ProductPrice = z.infer<typeof productPriceSchema>;
export type ProductRow = z.infer<typeof productRowSchema>;
export type ProductList = z.infer<typeof productListSchema>;
export type ProductDetail = z.infer<typeof productDetailSchema>;
export type ProductCreateInput = z.input<typeof productCreateSchema>;
export type ProductUpdateInput = z.input<typeof productUpdateSchema>;
export type PriceInput = z.input<typeof priceInputSchema>;
export type PriceStage = z.infer<typeof priceStageSchema>;
export type ShopPriceCheck = z.infer<typeof shopPriceCheckSchema>;
export type BookPackages = z.infer<typeof bookPackagesSchema>;
export type BookPackagesInput = z.input<typeof bookPackagesInputSchema>;
